import React, { useCallback, useMemo, useContext } from 'react'
import { Checkbox, makeStyles } from '@material-ui/core'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import BoardingWizardContext from './BoardingWizardContext'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import { FONT_WEIGHTS } from '../../../utils'
import { FOOD_SCHEDULE_OPTIONS } from './constants'
import StyledAlert from '../../Common/StyledAlert'

const EMPTY_SCHEDULE_ERROR = 'Select at least one meal time. Dogs must have at least one meal each day.'

const useStyles = makeStyles(() => ({
	checkBoxContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem',
		marginTop: '1rem',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& .MuiCheckbox-root': {
			padding: 0
		}
	}
}))

const FoodSchedule = (props) => {
	const classes = useStyles()
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(BoardingWizardContext)
	const {
		attr,
		_id: pet_id,
		name,
		copy_to_all_attr,
		petNames,
		first_view_of_multiple = false
	} = props
	const { loading } = useBoardingVisit()

	const displayName = wizard_state[copy_to_all_attr] === true ? petNames : name

	const title = useMemo(
		() => `Confirm ${displayName}'s meal schedule with the client`,
		[displayName]
	)

	const showCopyToAllCheckbox = useMemo(() => {
		return (
			first_view_of_multiple && wizard_state['pets'].length > 1 && copy_to_all_attr && copy_to_all_attr > ''
		)
	}, [first_view_of_multiple, wizard_state, copy_to_all_attr])

	const petSchedule = useMemo(() => {
		return wizard_state[attr]?.[pet_id] || []
	}, [wizard_state, attr, pet_id])

	const hasNoMealsSelected = petSchedule.length === 0

	const handleCopyToAll = useCallback(
		(e) => {
			if (e.target && e.target.checked !== undefined) {
				const newValues = { ...wizard_state }
				newValues[copy_to_all_attr] = e.target.checked
				setWizardAttributeAndValues(newValues)
			}
		},
		[wizard_state, setWizardAttributeAndValues, copy_to_all_attr]
	)

	const handleSelection = useCallback(
		(time) => {
			const newValues = { ...wizard_state }
			const schedules = { ...(newValues[attr] || {}) }
			const currentSchedule = schedules[pet_id] || []
			const isSelected = currentSchedule.includes(time)

			schedules[pet_id] = isSelected
				? currentSchedule.filter((t) => t !== time)
				: [...currentSchedule, time]

			newValues[attr] = schedules
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues, attr, pet_id]
	)

	const options = useMemo(() => {
		return FOOD_SCHEDULE_OPTIONS.map(({ title, time }) => ({
			title,
			description: '',
			disabled: loading,
			selected: petSchedule.includes(time),
			onSelected: () => handleSelection(time)
		}))
	}, [handleSelection, loading, petSchedule])

	return (
		<SectionFormContainer title={title}>
			<OptionsMenu options={options} />
			{hasNoMealsSelected ? (
				<StyledAlert style={{ marginTop: '.8rem' }} type="error" text={EMPTY_SCHEDULE_ERROR} />
			) : null}
			{showCopyToAllCheckbox ? (
				<div className={classes.checkBoxContainer}>
					<Checkbox onChange={handleCopyToAll} checked={wizard_state[copy_to_all_attr] === true} />
					<div>Copy schedule for all dogs</div>
				</div>
			) : null}
		</SectionFormContainer>
	)
}

export default FoodSchedule
